<app-navbar />
<!--
   Ordered by what an owner comes for: the shop's state at a glance, what its
   customers search for, then setup with a search to try (first, until the
   widget works) and the shop's settings.
-->
<main class="container relative md:py-32 py-24 min-h-screen max-w-4xl flex flex-col gap-6">
   <a routerLink="/business" class="text-sm text-cyan-600 hover:text-cyan-700 font-semibold">
      <i class="mdi mdi-arrow-left" aria-hidden="true"></i> {{ 'pages.shops-page.title' | translate }}
   </a>

   @if (stats; as s) {
      <!-- State at a glance -->
      <header class="flex flex-col gap-2">
         <h1 class="text-3xl font-bold [overflow-wrap:anywhere]">{{ s.shop.name }}</h1>
         <p class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600 dark:text-slate-300" id="status">
            @if (s.shop.plan === 'pro') {
               <span class="font-semibold text-cyan-700 dark:text-cyan-300"><i class="mdi mdi-star" aria-hidden="true"></i> {{ 'pages.shop-page.plan.pro' | translate }}</span>
            } @else {
               <span class="font-semibold">{{ 'pages.shop-page.plan.free' | translate }}</span>
            }
            @if (installed) {
               <span class="text-emerald-600"><i class="mdi mdi-check-circle" aria-hidden="true"></i> {{ 'pages.shop-page.status.working' | translate }}</span>
            } @else {
               <span class="text-amber-600"><i class="mdi mdi-clock-outline" aria-hidden="true"></i> {{ 'pages.shop-page.status.waiting' | translate }}</span>
            }
            <span>{{ 'pages.shop-page.status.products' | translate: { count: num(s.shop.productCount) } }}</span>
         </p>
         @if (s.shop.aiPaused) {
            <p id="plan-paused" class="text-amber-600 text-sm">{{ 'pages.shop-page.plan.paused' | translate }}</p>
         }
      </header>

      @if (!installed) {
         <ng-container [ngTemplateOutlet]="setup" />
      }

      <!-- What customers search for -->
      <section class="glass rounded-3xl p-6 flex flex-col gap-5" aria-labelledby="insights-title">
         <h2 id="insights-title" class="text-xl font-semibold">{{ 'pages.shop-page.insights.title' | translate }}</h2>
         <div class="grid sm:grid-cols-2 gap-x-8 gap-y-5">
            <div class="flex flex-col gap-3 min-w-0" id="top-queries">
               <h3 class="font-semibold">{{ 'pages.shop-page.top.title' | translate }}</h3>
               <ol class="flex flex-col gap-1">
                  @for (q of s.top; track q.query) {
                     <li class="flex justify-between gap-3"><span class="min-w-0 [overflow-wrap:break-word]">{{ q.query }}</span>
                        <span class="text-slate-500 whitespace-nowrap">{{ 'pages.shop-page.stats.times' | translate: { count: num(q.count) } }}</span></li>
                  } @empty {
                     <li class="text-slate-500">{{ 'pages.shop-page.stats.empty' | translate }}</li>
                  }
               </ol>
            </div>
            <div class="flex flex-col gap-3 min-w-0 border-t sm:border-t-0 sm:border-l border-slate-200 dark:border-slate-700 pt-5 sm:pt-0 sm:pl-8" id="not-found">
               <h3 class="font-semibold">{{ 'pages.shop-page.notFound.title' | translate }}</h3>
               <p class="text-sm text-slate-500 dark:text-slate-400">{{ 'pages.shop-page.notFound.hint' | translate }}</p>
               @if (s.notFoundLocked) {
                  <p id="not-found-locked">{{ 'pages.shop-page.notFound.locked' | translate: { count: s.notFoundCount } }}</p>
                  <a [href]="upgradeMailto" class="text-cyan-600 hover:text-cyan-700 font-semibold">{{ 'pages.shop-page.plan.upgrade' | translate }}</a>
               } @else {
                  <ol class="flex flex-col gap-1">
                     @for (q of s.notFound; track q.query) {
                        <li class="flex justify-between gap-3"><span class="min-w-0 [overflow-wrap:break-word]">{{ q.query }}</span>
                           <span class="text-slate-500 whitespace-nowrap">{{ 'pages.shop-page.stats.times' | translate: { count: num(q.noResults) } }}</span></li>
                     } @empty {
                        <li class="text-slate-500">{{ 'pages.shop-page.stats.empty' | translate }}</li>
                     }
                  </ol>
               }
            </div>
            <div class="flex flex-col gap-3 min-w-0 sm:col-span-2 border-t border-slate-200 dark:border-slate-700 pt-5" id="chosen">
               <h3 class="font-semibold">{{ 'pages.shop-page.recommendations.picks' | translate }}</h3>
               @if (s.picksLocked) {
                  <p id="recommendations-locked">{{ 'pages.shop-page.recommendations.locked' | translate }}</p>
                  <a [href]="upgradeMailto" class="text-cyan-600 hover:text-cyan-700 font-semibold">{{ 'pages.shop-page.plan.upgrade' | translate }}</a>
               } @else {
                  <p class="text-sm text-slate-500 dark:text-slate-400">{{ 'pages.shop-page.recommendations.picksHint' | translate }}</p>
                  <ol class="flex flex-col gap-1" id="picks">
                     @for (p of s.picks; track p.productId) {
                        <li class="flex justify-between gap-3">
                           <a [href]="p.url" target="_blank" rel="noopener" class="min-w-0 [overflow-wrap:break-word] hover:text-cyan-600">{{ p.title }}</a>
                           <span class="text-slate-500 whitespace-nowrap">{{ 'pages.shop-page.stats.times' | translate: { count: num(p.count) } }}</span>
                        </li>
                     } @empty {
                        <li class="text-slate-500">{{ 'pages.shop-page.stats.empty' | translate }}</li>
                     }
                  </ol>
               }
            </div>
         </div>
      </section>

      @if (installed) {
         <ng-container [ngTemplateOutlet]="setup" />
      }

      <!-- The shop and its plan -->
      <section class="glass rounded-3xl p-6 flex flex-col gap-5" aria-labelledby="settings-title">
         <h2 id="settings-title" class="text-xl font-semibold">{{ 'pages.shop-page.settings.title' | translate }}</h2>

         <div class="flex flex-col gap-2">
            <h3 class="font-semibold">{{ 'pages.shop-page.catalogue.title' | translate }}</h3>
            <p class="text-slate-600 dark:text-slate-300">{{ refreshText(s.shop.autoRefreshDays) | translate: {
               count: num(s.shop.productCount), source: ('pages.shop-page.source.' + s.shop.source | translate), date: date(s.shop.importedAt) } }}</p>
            @if (s.shop.nextManualRefreshAt) {
               <p class="text-sm text-slate-500 dark:text-slate-400">{{ 'pages.shop-page.catalogue.nextRefresh' | translate: { date: date(s.shop.nextManualRefreshAt) } }}</p>
            }
            @if (s.shop.importError) {
               <p class="text-amber-600">{{ 'pages.shop-page.catalogue.error' | translate: { date: date(s.shop.importedAt) } }}</p>
            }
            @if (errorKey) {
               <p class="text-red-500 text-sm" role="alert">{{ errorKey | translate }}</p>
            }
            <button type="button" (click)="refresh()" [disabled]="refreshing || !!s.shop.nextManualRefreshAt"
               class="self-start inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl px-4 py-2 transition disabled:bg-slate-300 disabled:text-slate-500 dark:disabled:bg-slate-700 dark:disabled:text-slate-400 disabled:cursor-not-allowed">
               @if (refreshing) {
                  <div class="spinner spinner-sm inline-block"></div>
               }
               {{ 'pages.shop-page.catalogue.refresh' | translate }}
            </button>
         </div>

         <div class="flex flex-col gap-2 border-t border-slate-200 dark:border-slate-700 pt-5" id="plan">
            <h3 class="font-semibold">{{ 'pages.shop-page.plan.title' | translate }}</h3>
            @if (s.shop.plan === 'pro') {
               <p class="text-slate-600 dark:text-slate-300">{{ 'pages.shop-page.plan.proText' | translate: { count: num(s.shop.productCap) } }}</p>
            } @else {
               <p class="text-slate-600 dark:text-slate-300">{{ 'pages.shop-page.plan.freeText' | translate: { count: num(s.shop.productCap), searches: num(s.shop.monthlySearches) } }}</p>
               <p id="plan-usage">{{ 'pages.shop-page.plan.usage' | translate: { used: num(s.shop.searchesThisMonth), limit: num(s.shop.monthlySearches) } }}</p>
               <a [href]="upgradeMailto" class="self-start bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl px-4 py-2">
                  {{ 'pages.shop-page.plan.upgrade' | translate }}
               </a>
            }
         </div>

         <div class="border-t border-slate-200 dark:border-slate-700 pt-5">
            <button type="button" (click)="remove()" id="remove"
               class="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl px-4 py-2 transition">
               <i class="mdi mdi-delete-outline" aria-hidden="true"></i>
               {{ 'pages.shop-page.remove' | translate }}
            </button>
         </div>
      </section>

      <!-- Setup: a search to try, the search line, and similar products for product pages -->
      <ng-template #setup>
         <section class="glass rounded-3xl p-6 flex flex-col gap-5" id="install">
            <div class="flex flex-col gap-3" id="try">
               <h2 class="text-xl font-semibold">{{ 'pages.shop-page.try.title' | translate }}</h2>
               <app-shop-search [shopId]="s.shop.id" [similar]="!s.picksLocked" [examples]="true" />
            </div>
            <div class="flex flex-col gap-3 border-t border-slate-200 dark:border-slate-700 pt-5">
               <h3 class="font-semibold">{{ 'pages.shop-page.install.title' | translate }}</h3>
               <p class="text-slate-600 dark:text-slate-300">{{ 'pages.shop-page.install.text' | translate: { host: s.shop.hosts[0] } }}</p>
               <div class="flex gap-2 items-stretch min-w-0">
                  <code id="snippet" class="flex-1 min-w-0 overflow-x-auto whitespace-nowrap bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2 text-sm">{{ snippet }}</code>
                  <button type="button" (click)="copy()" class="shrink-0 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl px-4">
                     {{ (copied ? 'pages.shop-page.install.copied' : 'pages.shop-page.install.copy') | translate }}
                  </button>
               </div>
               <p class="text-sm text-slate-500 dark:text-slate-400">{{ 'pages.shop-page.install.custom' | translate }}</p>
               @if (s.shop.lastWidgetSearchAt) {
                  <p class="text-emerald-600"><i class="mdi mdi-check-circle" aria-hidden="true"></i>
                     {{ 'pages.shop-page.install.active' | translate: { date: date(s.shop.lastWidgetSearchAt) } }}</p>
               } @else {
                  <p class="text-slate-500">{{ 'pages.shop-page.install.waiting' | translate }}</p>
               }
            </div>
            @if (!s.picksLocked) {
               <div class="flex flex-col gap-3 border-t border-slate-200 dark:border-slate-700 pt-5" id="recommendations">
                  <h3 class="font-semibold">{{ 'pages.shop-page.recommendations.title' | translate }}</h3>
                  <p class="text-slate-600 dark:text-slate-300">{{ 'pages.shop-page.recommendations.text' | translate }}</p>
                  <div class="flex gap-2 items-stretch min-w-0">
                     <code id="similar-snippet" class="flex-1 min-w-0 overflow-x-auto whitespace-nowrap bg-slate-100 dark:bg-slate-800 rounded-xl px-3 py-2 text-sm">{{ similarSnippet }}</code>
                     <button type="button" (click)="copySimilar()" class="shrink-0 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold rounded-xl px-4">
                        {{ (copiedSimilar ? 'pages.shop-page.install.copied' : 'pages.shop-page.install.copy') | translate }}
                     </button>
                  </div>
               </div>
            }
         </section>
      </ng-template>
   } @else if (missing) {
      <p class="text-slate-500">{{ 'errors.general' | translate }}</p>
   } @else {
      <div class="spinner"></div>
   }
</main>
<app-footer />
